/**
 * QC charts: shared UI pieces in the analyzer's style — card shell,
 * view toggles, legends, wicket marks, the green field and phase bands.
 */
import {
  BOUNDARY_R, FC, MARK_EDGE, NON_STRIKER_Y, RING_R, SECTORS, STRIKER_Y, annulus, slice, placeShots, outcomeColour, LENGTH_BANDS, BAND_BY_KEY, LINE_IDX, LINES, jitter, unit,
} from "./qcData";

// ── Shared pieces (the analyzer's card shell and toggles) ────────────
export function Card({ title, subtitle, controls, footer, children, wide }) {
  return (
    <section className={`bg-bg-secondary border border-border-default rounded-xl shadow-sm overflow-hidden min-w-0 ${wide ? "xl:col-span-2" : ""}`}>
      <header className="px-4 py-3 border-b border-border-default flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="min-w-0 flex-1">
          <h3 className="text-text-primary font-semibold tracking-tight text-[15px]">{title}</h3>
          {subtitle && <p className="text-text-secondary text-[11px] leading-snug mt-0.5">{subtitle}</p>}
        </div>
        {controls && <div className="flex flex-wrap items-center gap-2">{controls}</div>}
      </header>
      <div className="p-4">{children}</div>
      {footer && <div className="px-4 pb-3 -mt-1 text-[10px] text-text-secondary leading-snug">{footer}</div>}
    </section>
  );
}

export function Empty({ children }) {
  return <p className="text-xs text-text-secondary py-10 text-center">{children}</p>;
}

// The analyzer's view switch (Lines | Sectors, Grid | Pitch).
export function Toggle({ value, onChange, options, label }) {
  return (
    <div role="group" aria-label={label}
      className="inline-flex rounded-md border border-border-default overflow-hidden text-[10px]">
      {options.map((o) => (
        <button key={o.key} type="button" onClick={() => onChange(o.key)} aria-pressed={value === o.key}
          className={`px-2 py-0.5 transition ${value === o.key
            ? "bg-emerald-600/80 text-white font-semibold"
            : "text-text-secondary hover:text-text-primary"}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Swatch({ item }) {
  if (item.shape === "x") return <XMark size={10} />;
  return <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0" style={{ background: item.color, boxShadow: `0 0 0 1px ${MARK_EDGE}` }} />;
}

export function Legend({ items, counts }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-text-secondary">
      {items.map((it) => (
        <span key={it.label} className="inline-flex items-center gap-1.5">
          <Swatch item={it} />
          {it.label}
          {counts && <span className="text-text-primary tabular-nums">{counts[it.key] || 0}</span>}
        </span>
      ))}
    </div>
  );
}

// A white ✕ on a dark disc — reads on the green field and on the card.
export function XMark({ size = 10 }) {
  return (
    <svg width={size} height={size} viewBox="-5 -5 10 10" className="shrink-0" aria-hidden>
      <circle r="4.6" fill="rgba(15,23,42,0.85)" />
      <path d="M-2.2,-2.2 L2.2,2.2 M2.2,-2.2 L-2.2,2.2" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}
export function SvgX({ x, y, s = 1.5 }) {
  return (
    <g pointerEvents="none">
      <circle cx={x} cy={y} r={s * 1.25} fill="rgba(15,23,42,0.85)" />
      <path d={`M${x - s * 0.62},${y - s * 0.62} L${x + s * 0.62},${y + s * 0.62} M${x + s * 0.62},${y - s * 0.62} L${x - s * 0.62},${y + s * 0.62}`}
        stroke="#fff" strokeWidth={s * 0.32} strokeLinecap="round" />
    </g>
  );
}

// Hover tooltip: an absolutely-positioned box inside a relative wrapper.
export function Field({ children }) {
  return (
    <>
      <circle cx={FC} cy={FC} r={BOUNDARY_R} fill="#0d3218" stroke="rgba(255,255,255,0.15)" strokeWidth="0.4" />
      {SECTORS.map((s, i) => (
        <path key={`o${i}`} d={annulus(s.start, s.start + 45, RING_R, BOUNDARY_R)}
          fill={i % 2 ? "#1a5b2e" : "#12401f"} stroke="rgba(255,255,255,0.08)" strokeWidth="0.15" />
      ))}
      {SECTORS.map((s, i) => (
        <path key={`i${i}`} d={slice(s.start, s.start + 45, RING_R)}
          fill={i % 2 ? "#2d7548" : "#215b30"} stroke="rgba(255,255,255,0.08)" strokeWidth="0.15" />
      ))}
      {children}
      <circle cx={FC} cy={FC} r={RING_R} fill="none" stroke="rgba(255,255,255,0.65)" strokeWidth="0.3" strokeDasharray="1 0.8" pointerEvents="none" />
      <rect x="47" y={STRIKER_Y - 7} width="6" height={NON_STRIKER_Y - STRIKER_Y + 14} rx="0.4"
        fill="rgba(210,180,140,0.6)" stroke="rgba(255,255,255,0.4)" strokeWidth="0.15" pointerEvents="none" />
      <line x1="45" y1={STRIKER_Y} x2="55" y2={STRIKER_Y} stroke="rgba(255,255,255,0.5)" strokeWidth="0.2" />
      <line x1="45" y1={NON_STRIKER_Y} x2="55" y2={NON_STRIKER_Y} stroke="rgba(255,255,255,0.5)" strokeWidth="0.2" />
      <circle cx={FC} cy={STRIKER_Y} r="1.6" fill="#dc2626" stroke="#7f1d1d" strokeWidth="0.25" />
      <circle cx={FC} cy={NON_STRIKER_Y} r="1.6" fill="#dc2626" stroke="#7f1d1d" strokeWidth="0.25" />
    </>
  );
}


export function PhaseBands({ phases, x, y0, y1, width }) {
  return phases.map((p) => {
    const xa = x(p.from - 1), xb = x(p.to);
    return (
      <g key={p.label} pointerEvents="none">
        <rect x={xa} y={y0} width={Math.max(0, Math.min(xb, width) - xa)} height={y1 - y0} fill="var(--qc-phase)" />
        <text x={xa + 3} y={y0 + 7} fontSize="6.5" className="fill-text-secondary" style={{ textTransform: "uppercase", letterSpacing: 0.5 }}>{p.label}</text>
      </g>
    );
  });
}



// ── Small multiples ──────────────────────────────────────────────────

// A small wagon wheel for one player's shots. `lh` draws it in the
// left-hander's view (shots are placed in the right-hander's frame, so
// the finished picture is mirrored).
export function MiniWagon({ balls, lh = false, size = "100%" }) {
  const placed = placeShots(balls).filter((p) => p.b.offBat > 0 || p.b.wicket);
  const px = (x) => (lh ? 2 * FC - x : x);
  return (
    <svg viewBox="0 0 100 100" style={{ width: size }} className="h-auto block" role="img" aria-label="Wagon wheel">
      <Field />
      {placed.map((p) => (
        <line key={p.b.i} x1={FC} y1={STRIKER_Y} x2={px(p.x)} y2={p.y} stroke={outcomeColour(p.b)}
          strokeWidth={p.b.offBat >= 4 ? 0.9 : 0.7} strokeOpacity="0.9" strokeLinecap="round" />
      ))}
      {placed.filter((p) => p.b.wicket).map((p) => <SvgX key={`w${p.b.i}`} x={px(p.x)} y={p.y} s={2.2} />)}
    </svg>
  );
}

// A small pitch map: length bands down the pitch, one dot per ball in its
// coded cell. No % boxes — the card around it carries the numbers.
export function MiniPitch({ balls }) {
  const X0 = 10, X1 = 90, TOP = 8, MY = 7.2;
  const yOf = (m) => TOP + m * MY;
  const strip = (X1 - X0) / LINES.length;
  const pts = balls.filter((b) => BAND_BY_KEY[b.length]);
  return (
    <svg viewBox={`0 0 100 ${yOf(12) + 3}`} className="w-full h-auto block" role="img" aria-label="Pitch map">
      <rect x="0" y="0" width="100" height={yOf(12) + 3} rx="3" fill="#14532d" />
      {LENGTH_BANDS.map((bd) => (
        <rect key={bd.key} x={X0} y={yOf(bd.from)} width={X1 - X0} height={yOf(bd.to) - yOf(bd.from)} fill={bd.fill} fillOpacity="0.22" />
      ))}
      <line x1={X0} y1={TOP} x2={X1} y2={TOP} stroke="#fff" strokeOpacity="0.7" strokeWidth="0.5" />
      {[-1, 0, 1].map((k) => <rect key={k} x={50 + k * 1.8 - 0.5} y={TOP - 6} width="1" height="6" fill="#facc15" />)}
      {pts.map((b) => {
        const bd = BAND_BY_KEY[b.length];
        const cx = X0 + strip * ((LINE_IDX[b.line] ?? 3) + 0.5) + jitter(b.i * 2 + 1, strip * 0.36);
        const cy = yOf(bd.from + (bd.to - bd.from) * (0.15 + 0.7 * unit(b.i * 2 + 2)));
        return b.wicket
          ? <SvgX key={b.i} x={cx} y={cy} s={2} />
          : <circle key={b.i} cx={cx} cy={cy} r="1.5" fill={outcomeColour(b)} stroke={MARK_EDGE} strokeWidth="0.3" />;
      })}
    </svg>
  );
}

// Tab strip across the top of the charts view.
export function Tabs({ value, onChange, tabs }) {
  return (
    <div role="tablist" className="flex flex-wrap gap-1 border-b border-border-default">
      {tabs.map((t) => (
        <button key={t.key} type="button" role="tab" aria-selected={value === t.key} onClick={() => onChange(t.key)}
          className={`px-3 py-1.5 text-xs -mb-px border-b-2 transition ${value === t.key
            ? "border-emerald-500 text-text-primary font-semibold"
            : "border-transparent text-text-secondary hover:text-text-primary"}`}>
          {t.label}
          {t.badge != null && (
            <span className={`ml-1.5 rounded-full px-1.5 py-px text-[10px] tabular-nums ${t.badgeTone === "warn"
              ? "bg-amber-500/20 text-amber-300" : "bg-bg-secondary text-text-secondary"}`}>{t.badge}</span>
          )}
        </button>
      ))}
    </div>
  );
}

// A number tile, as on the summary strip.
export function Tile({ label, value, sub, tone }) {
  return (
    <div className="rounded-lg border border-border-default bg-bg-secondary px-3 py-2 min-w-0">
      <div className="text-[9px] uppercase tracking-wider text-text-secondary">{label}</div>
      <div className={`text-lg font-semibold tabular-nums leading-tight ${tone === "warn" ? "text-amber-400" : "text-text-primary"}`}>{value}</div>
      {sub != null && <div className="text-[10px] text-text-secondary tabular-nums truncate">{sub}</div>}
    </div>
  );
}

// Inline horizontal bar used inside tables.
export function Bar({ value, max, color = "var(--qc-s1)", label }) {
  return (
    <div className="flex items-center gap-1.5 min-w-0">
      <div className="h-2 rounded-r-[3px] shrink-0" style={{ width: `${max ? (value / max) * 62 : 0}%`, minWidth: value ? 2 : 0, background: color }} />
      {label != null && <span className="text-[10px] text-text-secondary tabular-nums whitespace-nowrap">{label}</span>}
    </div>
  );
}
